import React from 'react';
import { styled } from 'react-free-style';

/**
 * Dismissible page-level banner that signals a partial failure on the
 * project detail page. Complements the inline SectionError cards rendered
 * inside each affected tab — the banner gives the user immediate,
 * tab-agnostic awareness that something is off, while the inline cards
 * carry the per-tab detail when they visit the actual tab.
 *
 * The copy stays intentionally generic ("Some sections of this project
 * failed to load.") rather than listing the specific failing sections.
 * Rationale: the inline cards already carry the "which tab" information,
 * and a generic banner scales cleanly whether 1 or 7 endpoints failed
 * without reading like an incident report.
 *
 * The parent is responsible for conditionally mounting this component when
 * there's actually something to announce.
 *
 * Props:
 *   message    - optional override for the banner copy
 *   onRetry    - handler for the Retry button; omitted when not supplied
 *   onDismiss  - handler for the × button; omitted when not supplied
 *   isRetrying - disables Retry and swaps the label while a retry is in flight
 */
const SectionErrorBanner = ({
  styles,
  message = 'Some sections of this project failed to load.',
  onRetry,
  onDismiss,
  isRetrying = false,
}) => {
  return (
    <div className={styles.container} role="alert" data-testid="section-error-banner">
      <i className={`fa fa-exclamation-triangle ${styles.icon}`} aria-hidden="true" />
      <span className={styles.message}>{message}</span>
      <div className={styles.actions}>
        {onRetry && (
          <button
            type="button"
            className={styles.retryButton}
            onClick={onRetry}
            disabled={isRetrying}
            data-testid="section-error-banner-retry"
          >
            {isRetrying ? 'Retrying…' : 'Retry'}
          </button>
        )}
        {onDismiss && (
          <button
            type="button"
            className={styles.dismissButton}
            onClick={onDismiss}
            aria-label="Dismiss"
            data-testid="section-error-banner-dismiss"
          >
            ×
          </button>
        )}
      </div>
    </div>
  );
};

const withStyles = styled({
  // Card-style container so the banner matches the look of the inline
  // SectionError cards and the surrounding page cards, instead of reading
  // as an OS-level system bar. Width is still controlled by the parent
  // (project-view wraps us in styles.content → sizes.container).
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    background: '#fff8e1',
    border: '1px solid #e0c060',
    borderRadius: '4px',
    padding: '10px 16px',
    margin: '10px 0',
    fontSize: '14px',
  },
  icon: {
    color: '#b58900',
    fontSize: '16px',
  },
  message: {
    flex: 1,
    color: '#333',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  retryButton: {
    cursor: 'pointer',
    padding: '4px 14px',
    fontSize: '13px',
    border: '1px solid #333',
    background: 'white',
    textTransform: 'uppercase',
    '&:hover': {
      background: '#f5f5f5',
    },
    '&:disabled': {
      cursor: 'not-allowed',
      opacity: 0.6,
    },
    '&:focus': {
      outline: 'none',
    },
  },
  dismissButton: {
    cursor: 'pointer',
    padding: '0 8px',
    fontSize: '20px',
    lineHeight: 1,
    border: 'none',
    background: 'transparent',
    color: '#555',
    '&:hover': {
      color: '#000',
    },
    '&:focus': {
      outline: 'none',
    },
  },
});

export default withStyles(SectionErrorBanner);
